import { useState } from "react";
import { getPriorityExplanation, getPriorityHistory, submitPriorityFeedback } from "./PriorityService";
import { useToast } from "../ToastContext";

const FEEDBACK_OPTIONS = [
  { value: "TOO_LOW", label: "👇 Too low" },
  { value: "ACCURATE", label: "👍 Accurate" },
  { value: "TOO_HIGH", label: "👆 Too high" },
];

export const ANALYZER_LABELS = {
  deadline: "⏰ Deadline",
  importance: "⭐ Importance",
  dependency: "🔗 Dependencies",
  consequence: "💰 Consequence",
  goal: "🎯 Goal Alignment",
  calendar: "📅 Calendar",
  effort: "⚙️ Effort",
  behavior: "📊 Behavior",
  context: "🧩 Cross-Module",
};

const LEVEL_CLASS = {
  CRITICAL: "pri-level--critical",
  HIGH: "pri-level--high",
  MEDIUM: "pri-level--medium",
  LOW: "pri-level--low",
  SOMEDAY: "pri-level--someday",
};

/**
 * Explainable priority scoring (Smart Planning Engine, Phase 3) — never a
 * bare number: level + real-data-grounded factor breakdown + a
 * recommendation sentence. Button-triggered rather than auto-loaded on
 * mount, since every call persists a new IntelligenceDecision row (the
 * spec's "record every decision" requirement) — auto-firing on every form
 * open would spam that history with duplicates nobody asked for.
 */
export default function PriorityPanel({ itemId }) {
  const toast = useToast();
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(null); // the chosen value, once sent for the current decision
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const analyze = async () => {
    setLoading(true);
    try {
      const result = await getPriorityExplanation(itemId);
      setExplanation(result);
      setFeedbackSubmitted(null);
    } catch {
      toast("Failed to compute priority", "error");
    } finally {
      setLoading(false);
    }
  };

  const giveFeedback = async (value) => {
    if (!explanation?.decisionId || feedbackLoading) return;
    setFeedbackLoading(true);
    try {
      await submitPriorityFeedback(itemId, explanation.decisionId, value);
      setFeedbackSubmitted(value);
      toast("Thanks — feedback recorded", "success");
    } catch {
      toast("Failed to record feedback", "error");
    } finally {
      setFeedbackLoading(false);
    }
  };

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && !history) {
      setHistoryLoading(true);
      try {
        setHistory(await getPriorityHistory(itemId));
      } catch {
        toast("Failed to load priority history", "error");
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  const sortedFactors = explanation
    ? [...explanation.factors].sort((a, b) => b.weightedContribution - a.weightedContribution)
    : [];

  return (
    <div className="pri-panel">
      {!explanation && (
        <button type="button" className="pri-analyze-btn" onClick={analyze} disabled={loading}>
          {loading ? "Analyzing…" : "🧠 Analyze Priority"}
        </button>
      )}

      {explanation && (
        <div className="pri-result">
          <div className="pri-header">
            <span className={`pri-level-badge ${LEVEL_CLASS[explanation.priorityLevel] || ""}`}>
              {explanation.priorityLevel}
            </span>
            <span className="pri-score">{Math.round(explanation.priorityScore)}/100</span>
            <span className="pri-confidence">
              {Math.round(explanation.confidence * 100)}% confidence
            </span>
            <button type="button" className="pri-reanalyze-btn" onClick={analyze} disabled={loading}>
              {loading ? "…" : "↻ Re-analyze"}
            </button>
          </div>

          <div className="pri-recommendation">{explanation.recommendation}</div>

          <div className="pri-feedback">
            <span className="pri-feedback-label">Was this priority right?</span>
            <div className="pri-feedback-buttons">
              {FEEDBACK_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`pri-feedback-btn ${feedbackSubmitted === opt.value ? "pri-feedback-btn--active" : ""}`}
                  onClick={() => giveFeedback(opt.value)}
                  disabled={feedbackLoading || !!feedbackSubmitted}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {feedbackSubmitted && <span className="pri-feedback-thanks">Thanks!</span>}
          </div>

          <div className="pri-factors">
            {sortedFactors.map(f => (
              <div key={f.analyzer} className={`pri-factor-row ${f.dataAvailable ? "" : "pri-factor-row--stub"}`}>
                <div className="pri-factor-head">
                  <span className="pri-factor-name">{ANALYZER_LABELS[f.analyzer] || f.analyzer}</span>
                  <span className="pri-factor-weight">weight {Math.round(f.weight * 100)}%</span>
                  <span className="pri-factor-score">{Math.round(f.rawScore)}/100</span>
                  {!f.dataAvailable && <span className="pri-factor-stub-tag">no data yet</span>}
                </div>
                {f.reasons?.map((r, idx) => (
                  <div key={idx} className="pri-factor-reason">{r}</div>
                ))}
              </div>
            ))}
          </div>

          <button type="button" className="pri-history-toggle" onClick={toggleHistory}>
            {showHistory ? "Hide history" : "Show past decisions"}
          </button>

          {showHistory && (
            <div className="pri-history">
              {historyLoading && <div className="pri-history-loading">Loading…</div>}
              {!historyLoading && history?.length === 0 && (
                <div className="pri-history-empty">No past decisions recorded.</div>
              )}
              {!historyLoading && history?.map(h => (
                <div key={h.id} className="pri-history-row">
                  <span className={`pri-level-badge pri-level-badge--sm ${LEVEL_CLASS[h.priorityLevel] || ""}`}>
                    {h.priorityLevel}
                  </span>
                  <span className="pri-history-score">{Math.round(h.priorityScore)}/100</span>
                  <span className="pri-history-date">
                    {h.createdAt ? new Date(h.createdAt).toLocaleString() : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
